import { useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { appToast } from '../../../components/AppToast'
import AppFormFooterBar from '../../../components/AppFormFooterBar'
import AppRichTextEditor from '../../../components/AppRichTextEditor'
import AppDropdown from '../../../components/AppDropdown'
import { useStudentLandingEntryScreen } from './hooks/useStudentLandingEntryScreen'

export default function StudentLandingEntryScreen() {
  const { state, actions } = useStudentLandingEntryScreen()

  useEffect(() => {
    if (state.error) appToast.error(state.error)
  }, [state.error])

  useEffect(() => {
    if (state.success) appToast.success(state.success)
  }, [state.success])

  const publicUrl = useMemo(() => {
    if (state.form.url) return state.form.url
    const compId = state.form.companyId || 1
    const id = state.entryId || 0
    if (id) {
      return `https://tezerp.com/StudentLandingPage.aspx?cust=${compId}&id=${id}`
    }
    return ''
  }, [state.form.url, state.form.companyId, state.entryId])

  const handleCopyUrl = async () => {
    if (!publicUrl) return
    try {
      await navigator.clipboard.writeText(publicUrl)
      appToast.success('Public landing page URL copied to clipboard!')
    } catch {
      appToast.error('Could not copy link to clipboard.')
    }
  }

  if (state.isLoading) {
    return (
      <div className="mx-auto flex w-full max-w-[1460px] items-center justify-center px-6 py-24 text-sm text-on-surface-variant font-medium">
        Loading landing page details...
      </div>
    )
  }

  const companyDropdownOptions = (state.options.companyOptions || []).map((opt) => ({
    value: String(opt.value),
    label: opt.label || opt.name || String(opt.value),
  }))

  const branchDropdownOptions = (state.options.branchOptions || []).map((opt) => ({
    value: String(opt.value),
    label: opt.label || opt.name || String(opt.value),
  }))

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-28">

      {/* ── Screen Header ──────────────────────────────────────────────── */}
      <section className="flex flex-col gap-3 border-b border-outline-variant/10 pb-5 xl:flex-row xl:items-end xl:justify-between">
        <div className="max-w-4xl">
          <div className="flex items-center gap-2 text-xs font-semibold text-on-surface-variant mb-1">
            <Link
              to="/admission/student-landing/list"
              className="inline-flex items-center gap-1 text-primary hover:underline"
            >
              <span className="material-symbols-outlined text-sm">arrow_back</span>
              Back to Landing Pages
            </Link>
          </div>
          <h1 className="text-4xl font-extrabold leading-[0.98] tracking-[-0.02em] text-on-surface">
            {state.isViewMode ? 'View Landing Page' : state.isEditMode ? 'Edit Landing Page' : 'Create Landing Page'}
          </h1>
          <p className="mt-1.5 text-xs text-on-surface-variant font-medium">
            {state.isViewMode
              ? 'Viewing the public landing page template and company/branch mappings.'
              : 'Configure landing page title, default branch mappings, and applicant admission form HTML content.'}
          </p>
        </div>
      </section>

      {/* ── Metadata Card ──────────────────────────────────────────────── */}
      <div className="rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-6 shadow-sm flex flex-col gap-5">
        <h3 className="text-xs font-black uppercase tracking-[0.14em] text-primary">
          Page Parameters & Default Institute
        </h3>

        <div className="grid grid-cols-12 gap-5">
          {/* Page Name */}
          <div className="col-span-12 md:col-span-4">
            <label className="mb-1.5 block text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">
              Page Name <span className="text-error">*</span>
            </label>
            <input
              type="text"
              value={state.form.name || ''}
              onChange={(e) => actions.patchForm('name', e.target.value)}
              disabled={state.isViewMode}
              className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-low px-4 py-2.5 text-sm font-semibold text-on-surface outline-none transition-all focus:border-primary focus:ring-1 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-70"
              placeholder="e.g. Science & Commerce Admissions 2026-27"
              minLength={5}
            />
            <span className="mt-1 block text-[11px] text-on-surface-variant/60 font-medium">
              Must be at least 5 characters (legacy validation).
            </span>
          </div>

          {/* Default Company */}
          <div className="col-span-12 md:col-span-4">
            <label className="mb-1.5 block text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">
              Default Company / Institute <span className="text-error">*</span>
            </label>
            <AppDropdown
              value={String(state.form.companyId || '')}
              onChange={(val) => actions.handleCompanyChange(val)}
              options={[
                { value: '', label: '-- Select Company --' },
                ...companyDropdownOptions,
              ]}
              disabled={state.isViewMode}
              placeholder="-- Select Company --"
            />
          </div>

          {/* Default Branch */}
          <div className="col-span-12 md:col-span-4">
            <label className="mb-1.5 block text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">
              Default Branch <span className="text-error">*</span>
            </label>
            <AppDropdown
              value={String(state.form.branchId || '')}
              onChange={(val) => actions.handleBranchChange(val)}
              options={[
                { value: '', label: '-- Select Branch --' },
                ...branchDropdownOptions,
              ]}
              disabled={state.isViewMode}
              placeholder="-- Select Branch --"
            />
          </div>
        </div>
      </div>

      {/* ── Public Applicant URL Card (When Available) ──────────────────── */}
      {publicUrl && (
        <div className="rounded-2xl border border-outline-variant/20 bg-surface-container-low/40 p-5 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <span className="material-symbols-outlined text-xl">link</span>
            </div>
            <div className="min-w-0">
              <h4 className="text-xs font-bold text-on-surface uppercase tracking-wider">
                Public Applicant Registration Link
              </h4>
              <p className="text-xs font-mono text-on-surface-variant truncate mt-0.5">
                {publicUrl}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={handleCopyUrl}
              className="inline-flex items-center gap-1.5 rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-3 py-1.5 text-xs font-bold text-on-surface hover:bg-surface-container-high transition-colors cursor-pointer"
            >
              <span className="material-symbols-outlined text-sm">content_copy</span>
              <span>Copy URL</span>
            </button>
            <a
              href={publicUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-xl bg-primary/10 border border-primary/20 px-3 py-1.5 text-xs font-bold text-primary hover:bg-primary/20 transition-colors"
            >
              <span className="material-symbols-outlined text-sm">open_in_new</span>
              <span>Open Page</span>
            </a>
          </div>
        </div>
      )}

      {/* ── Rich WYSIWYG HTML Content Card ──────────────────────────────── */}
      <div className="rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-6 shadow-sm flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-black uppercase tracking-[0.14em] text-primary">
            Landing Page HTML Content & Web Form Template
          </h3>
          <span className="text-[11px] font-semibold text-on-surface-variant/70">
            Design, HTML Source, and Preview modes available
          </span>
        </div>

        <AppRichTextEditor
          value={state.form.html || ''}
          onChange={(val) => actions.patchForm('html', val)}
          height={520}
          disabled={state.isViewMode}
          placeholder="Design or paste your admission landing page and applicant registration form HTML here..."
        />
      </div>

      {/* ── Persistent Standard Footer Action Bar ──────────────────────── */}
      <AppFormFooterBar
        cancelPath="/admission/student-landing/list"
        cancelLabel={state.isViewMode ? 'Back' : 'Cancel'}
        saveLabel={state.isEditMode ? 'Update Landing Page' : 'Save Landing Page'}
        onSaveExit={!state.isViewMode ? actions.handleSave : null}
        isSaving={state.isSaving}
      />
    </div>
  )
}
